import { useEffect, useState } from 'react';
import { Button, Form, message, Radio, Space } from 'antd';
import 'moment-timezone'
import { TextService } from '../../Services/TextService';
import { useAuth } from '../../Contexts/AuthContext';
import { BookyInput } from '../../Components/Forms/BookyInput';
import { CompanyService } from '../../Services/CompanyService';
import { BookySelectDropDown } from '../../Components/Forms/BookySelectDropDown';
import { isOnlyNumbers } from '../../Helpers/Validators';
import { FamilyMemberConfiguration } from '../../Types/FamilyMemberConfiguration';
import { FamilyMemberDisplayMode } from '../../Types/FamilyMemberDisplayMode';

const AccountForm = () => {
    const [event, setEvent] = useState([] as any);
    const [language, setLanguage] = useState("" as string);
    const [familyMemberEnabled, setFamilyMemberEnabled] = useState(false);

    const [form] = Form.useForm();
    const { user, refreshUser } = useAuth();

    const GetText = TextService.GetText;

    const FormCreateClick = async (values: any) => {

        if (
            values.organisationNumber &&
            values.organisationNumber.trim() !== "" &&
            (
                values.organisationNumber.length !== 10 ||
                !isOnlyNumbers(values.organisationNumber)
            )
        ) {
            message.error(GetText("AccountForm_OrganisationNumberError"));
            return;
        }

        await CompanyService.UpdateCompany({
            language: language,
            name: values.companyname,
            organisationNumber: values.organisationNumber,
            email: values.companyemail,
            smsSender: values.companysmssender,
            paymentConfiguration: values.paymentConfiguration,
            companyTypeConfiguration: values.companyTypeConfiguration,
            familyMemberConfiguration: values.familyMemberConfiguration,
            familyMemberDisplayMode: values.familyMemberDisplayMode ?? FamilyMemberDisplayMode.ShowFamilyMembers
        });
        await refreshUser();
        message.success(GetText("Common_SettingsUpdated"));
    };

    const handleChange = (value: string) => {
        setLanguage(value);
    };

    useEffect(() => {
        CompanyService.GetCompany().then(company => {
            handleChange(company.language!);
            form.setFieldValue("companyemail", company.email!)
            form.setFieldValue("companysmssender", company.smsSender!)
            form.setFieldValue("companyname", company.name!)
            form.setFieldValue("organisationNumber", company.organisationNumber!)
            form.setFieldValue("language", company.language!)
            form.setFieldValue("companyTypeConfiguration", company.companyTypeConfiguration!)
            form.setFieldValue("paymentConfiguration", company.paymentConfiguration!)
            form.setFieldValue("familyMemberConfiguration", company.familyMemberConfiguration ?? FamilyMemberConfiguration.Disabled)
            form.setFieldValue("familyMemberDisplayMode", company.familyMemberDisplayMode ?? FamilyMemberDisplayMode.ShowFamilyMembers)
            setFamilyMemberEnabled((company.familyMemberConfiguration ?? FamilyMemberConfiguration.Disabled) === FamilyMemberConfiguration.Enabled)
        });

    }, []);

    const languageOptions = [
        { value: 'se', label: GetText("AccountForm_Swedish") },
        { value: 'en', label: GetText("AccountForm_English") },
    ];

    const paymentOptions = [
        { value: 0, label: "Inga betalningar" },
        { value: 1, label: "Helårs betalningar" },
    ];

    const companyTypeOptions = [
        { value: 0, label: "Medlemshantering" },
        { value: 1, label: "Kundhantering" },
    ];

    const familyMemberOptions = [
        { value: 0, label: "Av" },
        { value: 1, label: "På" },
    ];

    const onFamilyMemberConfigurationChange = (opt: any) => {
        form.setFieldValue("familyMemberConfiguration", opt);
        setFamilyMemberEnabled(opt === FamilyMemberConfiguration.Enabled);
    };

    return (
        <Form
            colon={false}
            form={form}
            labelCol={{
                span: 3,
            }}
            onFinish={FormCreateClick}
            wrapperCol={{
                span: 6,
            }}
            layout="vertical"
        >
            <BookyInput label={GetText("AccountForm_CompanyName")} name="companyname" req></BookyInput>
            <BookyInput label={GetText("AccountForm_OrganisationNumber")} placeholder="Ange 10-siffrigt organisationsnummer" name="organisationNumber"></BookyInput>
            <BookyInput label={GetText("AccountForm_CompanyEmail")} req validateEmailIfFilledIn={true} name="companyemail" ></BookyInput>
            <BookyInput label={GetText("AccountForm_CompanySmsSender")} req validateNonAlphanumericTextIfFilledIn={true} name="companysmssender" extra={GetText("AccountForm_SmsSenderExtra")} ></BookyInput>

            <BookySelectDropDown label="Språk" handleChange={handleChange} name="language" options={languageOptions} />
            <BookySelectDropDown label="Betalningar" handleChange={(opt: any) => form.setFieldValue("paymentConfiguration", opt)} name="paymentConfiguration" options={paymentOptions} />
            <BookySelectDropDown label="Typ av organisation" handleChange={(opt: any) => form.setFieldValue("companyTypeConfiguration", opt)} name="companyTypeConfiguration" options={companyTypeOptions} />
            <BookySelectDropDown
                label="Familjemedlemmar"
                handleChange={onFamilyMemberConfigurationChange}
                name="familyMemberConfiguration"
                options={familyMemberOptions}
                help={GetText("AccountForm_FamilyMemberHelp")}
                helpTitle="Familjemedlemmar"
            />

            {familyMemberEnabled && (
                <Form.Item label={GetText("AccountForm_FamilyMemberDisplayModeLabel")} name="familyMemberDisplayMode">
                    <Radio.Group>
                        <Space direction="vertical">
                            <Radio value={FamilyMemberDisplayMode.ShowFamilyMembers}>{GetText("AccountForm_FamilyMemberDisplayMode_Show")}</Radio>
                            <Radio value={FamilyMemberDisplayMode.HideFamilyMembers}>{GetText("AccountForm_FamilyMemberDisplayMode_Hide")}</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            )}

            <Form.Item label={" "}>
                <Button data-testid="submit-button" style={{ float: 'left' }} size="middle" type="primary" htmlType="submit">
                    {GetText("AccountForm_LanguageSave")}
                </Button>
            </Form.Item>
        </Form>)

};

export default AccountForm;